import GenericQueue from '@/components/common/ed-board/ed-board-tools/wbo-client/util/generic-queue';
import {
    WboAnnotationData, WboBatchedBroadcastMsg, WboChildPointData,
    WboDelta,
    WboMsg
} from '@/components/common/ed-board/ed-board-tools/wbo-client/model/wbo-annotation.model';
import WboConnectionStatusObserver
    from '@/components/common/ed-board/ed-board-tools/wbo-client/interfaces/wbo-connection-status-observer.interface';
import _ from 'lodash';
import {nowInMicroseconds} from '@/components/common/ed-board/ed-board-tools/wbo-client/util/wbo-utils';

export default class WboPendingChangesQueue implements WboConnectionStatusObserver {
    private readonly outgoingOpsQueue: GenericQueue<WboDelta>;
    private readonly inflightOpsQueue: GenericQueue<WboDelta>;
    private isConnected: boolean;
    private START_COMPOSING_AT: number = 10;
    private MAX_GROUP_SIZE: number = 100;


    constructor(outgoingOpsQueue: GenericQueue<WboDelta>, isConnected = false) {
        this.outgoingOpsQueue = outgoingOpsQueue;
        this.inflightOpsQueue = new GenericQueue<WboDelta>();
        this.isConnected = isConnected;
    }

    /**
     * Packs the queue into a few deltas as possible. This traverse the backing array of the queue and tries to compose
     * deltas that can be composed. For example, if there are two deltas that are updates to the same annotation, they
     *
     */
    pack(): this {
        const deltasArray = this.outgoingOpsQueue.elements();
        this.clear();
        deltasArray.forEach((delta: WboDelta) => {
            this.composeOp(delta);
        });
        return this;
    }

    onSubscribe(): void {
    }

    setConnected(isConnected: boolean) {
        this.isConnected = isConnected;
    }

    enqueue(wboDelta: WboDelta) {
        if (this.isConnected) {
            if (this.outgoingOpsQueue.size() + 1 >= this.START_COMPOSING_AT) {
                if (this.outgoingOpsQueue.size() + 1 === this.START_COMPOSING_AT) {
                    this.pack();
                }
                this.composeOp(wboDelta);
            }
            else {
                this.outgoingOpsQueue.enqueue(wboDelta);   
            }
        }
        else {
            this.composeOp(wboDelta);
        }
    }

    /**
     * Enqueues the delta at the beginning of the queue. This is useful for re-sending the delta that was just sent to
     * the server, in case the server did not acknowledge it.
     */
    enqueueFirst(wboDelta: WboDelta) {
        if (this.isConnected) {
            this.outgoingOpsQueue.enqueueFirst(wboDelta);
        }
        else {
            this.composeOp(wboDelta);
        }
    }

    /**
     * Composes the delta with the rest of the deltas in the queue. This is done by checking the type of the delta and
     * the type of the data in the delta. The composition is done in place, so the delta is modified. The composition
     * is done in the following way:
     * - If the delta is of type 'broadcast' and the data type is 'delete', then the delta is removed from the queue.
     * - If the delta is of type 'broadcast' and the data type is 'update', then the delta is merged with the rest of the
     *  deltas that have the same id.
     *  - If the delta is of type 'broadcast' and the data type is 'child', then the child is added to the parent.
     *  - If the delta is of type 'broadcast' and the data type is 'line' or 'straight', then the delta is just added to
     *  the queue.
     *  - If the delta is of type 'removeAllByUser', then the queue is cleared and the delta is added to the queue.
     *
     */
    private composeOp(delta: WboDelta) {
        if (delta.opType === 'broadcast') {
            const msg = delta.msg as WboMsg;
            if (!msg?.data?.type) {
                return;
            }
            switch (msg.data.type) {
                case 'delete':
                    this.composeDelete(msg);
                    break;
                case 'update':
                    this.composeUpdate(msg);
                    break;
                case 'child':
                    this.composeChild(msg);
                    break;
                case 'finish':
                    break;
                case 'line':
                case 'straight': {
                    if (msg?.data?.id) {
                        this.outgoingOpsQueue.enqueue(  {opType: delta.opType, msg: delta.msg});
                    }
                    break;
                }
                default:
                    break;
            }

        }
        else if (delta.opType == 'removeAllByUser') {
            this.composeRemoveAllByUser(delta.msg as WboMsg);
        }
        else if (delta.opType == 'broadcastMultiple') {
            this.outgoingOpsQueue.enqueue(delta);
            return;
        }
        (delta.msg as WboMsg).operationId = nowInMicroseconds() + this.outgoingOpsQueue.size();
    }

    /**
     * Composition of a delta with opType: 'broadcast' and data type 'delete' just results in the deletion of the
     * referenced delta. If no such delta is found among the pending delta queue, then the deletion delta is just added
     * to the queue, since it might be found in the server side annotation list.
     */
    private composeDelete(msg: WboMsg) {
        if (!msg?.data?.id) {
            return;
        }
        // Remove the deltas that are to be deleted from the queue, with lodash
        const removedDeltas: WboDelta[] = _.remove(this.outgoingOpsQueue.elements(), (entry: WboDelta) => {
            if (entry.opType !== 'broadcast') {
                return false;
            }
            return (entry.msg as WboMsg).data?.id == msg.data.id;
        });
        if (!removedDeltas || removedDeltas.length == 0) {
            this.outgoingOpsQueue.enqueue({opType: 'broadcast', msg});
        }

    }

    private composeUpdate(msg: WboMsg) {
        if (!msg?.data?.id) {
            return;
        }
        const referencedDelta: WboDelta = _.find(this.outgoingOpsQueue.elements(), (entry: WboDelta) => {
            if (entry.opType !== 'broadcast') {
                return false;
            }
            return (entry.msg as WboMsg).data?.id == msg.data.id;
        });
        if (!referencedDelta) {
            // TODO: Check if there are any orphan updates  and merge this with that
            this.outgoingOpsQueue.enqueue({opType: 'broadcast', msg});
            return;
        }
        _.merge(referencedDelta.msg.data, _.omit(msg.data, ['type', 'tool']))
    }

    private composeChild(msg: WboMsg) {
        let childPointData = msg?.data as WboChildPointData;
        if (!childPointData?.parent) {
            return;
        }

        const parentDelta: WboDelta = _.find(this.outgoingOpsQueue.elements(),
            (entry: WboDelta) => {
                if (entry.opType !== 'broadcast') {
                    return false;
                }
                return (entry.msg as WboMsg).data?.id == childPointData.parent;
            });
        if (!parentDelta) {
            childPointData = {
                ...childPointData,
                operationId: msg.operationId ?? nowInMicroseconds()
            } as WboChildPointData;

            // Find the last delta of opType batchedBroadcast of the same parent
            const lastBatchedBroadcast: WboDelta = _.findLast(this.outgoingOpsQueue.elements(),
                (entry: WboDelta) => entry.opType === 'broadcastMultiple'
                    && ((entry.msg as WboBatchedBroadcastMsg).data[0] as WboChildPointData)?.parent == childPointData.parent);
            if (!lastBatchedBroadcast) {
                this.outgoingOpsQueue.enqueue({
                    opType: 'broadcastMultiple', msg: {
                        board: msg.board, clientId: msg.clientId, data: [childPointData]
                    }
                });
            }
            else {
                // If the last batched broadcast data array size is greater than MAX_GROUP_SIZE, then create a new
                // batched broadcast
                const lastBatchedBroadcastData = (lastBatchedBroadcast.msg as WboBatchedBroadcastMsg).data as WboChildPointData[];
                if (lastBatchedBroadcastData.length >= this.MAX_GROUP_SIZE) {
                    this.outgoingOpsQueue.enqueue({
                        opType: 'broadcastMultiple', msg: {
                            board: msg.board, clientId: msg.clientId, data: [childPointData]
                        }
                    });
                }
                else {
                    lastBatchedBroadcastData.push(childPointData);
                }
            }
            return;

        }
        const parentData = parentDelta.msg.data as WboAnnotationData & WboChildPointData;
        const childData = _.pick(msg.data, ['parent', 'tool', 'type', 'x', 'y']) as WboAnnotationData & WboChildPointData;
        if (Array.isArray(parentData._children)) {
            parentData._children.push(childData);
        } else {
            parentData._children = [childData];
        }
    }

    private composeRemoveAllByUser(msg: WboMsg) {
        this.outgoingOpsQueue.clear();
        this.outgoingOpsQueue.enqueue({opType: 'removeAllByUser', msg});
    }


    onConnect() {
        this.isConnected = true;
    }
    onDisconnect() {
        this.isConnected = false;
    }
    onReconnect() {
        this.isConnected = true;
    }

    queue() {
        return this.outgoingOpsQueue;
    }

    inflightOps() {
        return this.inflightOpsQueue;
    }                    

    clear() {
        this.outgoingOpsQueue.clear();
    }

    addInflightOp(wboDelta: WboDelta) {
        this.inflightOpsQueue.enqueue(wboDelta);
    }

    clearInflightOps() {
        this.inflightOpsQueue.clear();
    }
}